import { useMutation } from "@apollo/client";
import { createAjv } from "@jsonforms/core";
import { useState } from "react";
import { createKeywordGroup } from "../graphql/mutations/createKeywordGroup";
import { deleteKeywordGroup } from "../graphql/mutations/deleteKeywordGroup";
import { updateKeywordGroup } from "../graphql/mutations/updateKeywordGroup";
import {
  OUTLET_TYPE,
  keywordFormSchema,
} from "../pages/management/KeywordManagement/keywordFormSchema";
import { useToast } from "./useToast";

const ajv = createAjv({ useDefaults: true });
const validateKeyword = ajv.compile(keywordFormSchema);

export function useKeywordModal() {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState("add"); // "add", "edit", or "delete"
  const [formData, setFormData] = useState({});
  const [originalType, setOriginalType] = useState(null);
  const [errors, setErrors] = useState([]);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [executeCreateKeyword] = useMutation(createKeywordGroup);
  const [executeUpdateKeyword] = useMutation(updateKeywordGroup);
  const [executeDeleteKeyword] = useMutation(deleteKeywordGroup);
  const { toast, showToast, closeToast } = useToast();

  const openModal = (modalMode = "add", keywordData = {}) => {
    setMode(modalMode);
    setFormData(keywordData);
    setOriginalType(keywordData.type ?? null);
    setErrors([]);
    setSubmitAttempted(false);
    setIsOpen(true);
  };

  const closeModal = () => {
    setIsOpen(false);
    setFormData({});
    setOriginalType(null);
    setErrors([]);
    setSubmitAttempted(false);
    setMode("add");
  };

  const handleFormChange = (data, nextErrors = []) => {
    setFormData(data);
    if (submitAttempted) {
      setErrors(nextErrors);
    }
  };

  const handleSubmit = async (onSuccess) => {
    if (mode === "delete") {
      return handleDelete(onSuccess);
    }

    if (!validateKeyword(formData)) {
      setErrors(validateKeyword.errors || []);
      setSubmitAttempted(true);
      return { isSuccess: false, message: "Please fix the validation errors" };
    }

    setIsLoading(true);

    try {
      const hasTypeChanged = originalType !== null && originalType !== formData.type;
      const keywordPayload = {
        name: formData.name,
        slug: formData.slug,
        imageUrl: formData.imageUrl,
        type: formData.type,
        category: formData.category,
        keywords: formData.keywords || [],
        metadata:
          formData.type === OUTLET_TYPE || !hasTypeChanged
            ? formData.metadata ?? null
            : null,
      };

      let response;
      let message;

      if (mode === "edit" && formData.id) {
        response = await executeUpdateKeyword({
          variables: {
            id: formData.id,
            input: keywordPayload,
          },
        });
        message = "Keyword updated successfully";
      } else {
        response = await executeCreateKeyword({
          variables: { input: keywordPayload },
        });
        message = "Keyword created successfully";
      }

      setIsLoading(false);
      showToast(
        "success",
        "Keyword Saved",
        "The keyword has been saved successfully"
      );
      closeModal();

      if (onSuccess) {
        onSuccess();
      }

      return {
        isSuccess: true,
        message,
        data: response.data,
      };
    } catch (error) {
      setIsLoading(false);
      return {
        isSuccess: false,
        message: error.message || "An error occurred while saving the keyword",
      };
    }
  };

  const handleDelete = async (onSuccess) => {
    if (!formData.id) {
      return {
        isSuccess: false,
        message: "No keyword ID provided for deletion",
      };
    }

    setIsLoading(true);

    try {
      await executeDeleteKeyword({
        variables: {
          id: formData.id,
        },
      });

      setIsLoading(false);
      showToast(
        "success",
        "Keyword Deleted",
        "The keyword has been deleted successfully"
      );
      closeModal();

      if (onSuccess) {
        onSuccess();
      }

      return {
        isSuccess: true,
        message: "Keyword deleted successfully",
      };
    } catch (error) {
      setIsLoading(false);
      showToast(
        "error",
        "Delete Failed",
        error.message || "An error occurred while deleting the keyword"
      );
      return {
        isSuccess: false,
        message: error.message || "An error occurred while deleting the keyword",
      };
    }
  };

  return {
    isOpen,
    mode,
    formData,
    errors,
    submitAttempted,
    isLoading,
    openModal,
    closeModal,
    handleFormChange,
    handleSubmit,
    toast,
    showToast,
    closeToast,
  };
}
